import React, { useState } from 'react';
import Modal from './Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import Form from '../Form/Form';
import InputText from '../Form/Fields/InputText';
import InputDate from '../Form/Fields/InputDate';
import Textarea from '../Form/Fields/Textarea';
import InputProduct from '../Form/Fields/InputProduct';
import InputLaboratory from '../Form/Fields/InputLaboratory';
import InputQuantity from '../Form/Fields/InputQuantity';
import Select from '../Form/Fields/Select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faPills, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import Button from '../Form/Button';
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL } from '../../api';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toastCreated } from '../../utils/ToastLink';

import './CreateProductModal.scss';

function ProductFormContent({ stockLines, onAddLine, onRemoveLine, onSetProductName }) {
  const { setValue } = useFormContext();
  const [showMore, setShowMore] = useState(false);

  const STATUS_OPTIONS = [
    { value: 'MARKETED', label: 'Commercialisé' },
    { value: 'NOT_MARKETED', label: 'Non commercialisé' },
  ];

  const REFUNDABLE_OPTIONS = [
    { value: 'false', label: 'Non remboursable' },
    { value: 'true', label: 'Remboursable' },
  ];

  const handleProductChange = async (selected) => {
    if (!selected?.value) { onSetProductName(null); return; }

    const isExistingProduct = typeof selected.value === 'number' || /^\d+$/.test(String(selected.value));
    if (!isExistingProduct) { onSetProductName(null); return; }

    onSetProductName(selected.label);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/products/${selected.value}`, { withCredentials: true });
      if (data?.publicPrice != null) setValue('publicPrice', data.publicPrice);
      if (data?.barcode) setValue('barcode', data.barcode);
      if (data?.status) setValue('status', data.status);
      if (data?.presentation) setValue('presentation', data.presentation);
      if (data?.refundable != null) setValue('refundable', String(data.refundable));
      if (data?.dosage) setValue('dosage', data.dosage);
      if (data?.laboratory?.id) setValue('laboratoryId', data.laboratory.id);
      if (data?.hospitalPrice != null) setValue('hospitalPrice', data.hospitalPrice);
      if (data?.minimumStock != null) setValue('minimumStock', data.minimumStock);
      if (data?.family) setValue('family', data.family);
      if (data?.productType) setValue('productType', data.productType);
      if (data?.atcCode) setValue('atcCode', data.atcCode);
      if (data?.tableLetter) setValue('tableLetter', data.tableLetter);
      if (data?.composition) setValue('composition', data.composition);
      if (data?.indication) setValue('indication', data.indication);
      if (data?.dosagesAdministration) setValue('dosagesAdministration', data.dosagesAdministration);
      if (data?.contraindication) setValue('contraindication', data.contraindication);
      if (data?.warnings) setValue('warnings', data.warnings);
      if (data?.dependanceRisk) setValue('dependanceRisk', data.dependanceRisk);
      if (data?.psychoactiveSubstance) setValue('psychoactiveSubstance', data.psychoactiveSubstance);
    } catch (e) {
      console.error('Failed to fetch product details', e);
    }
  };

  return (
    <>
      <div className="cdm-section">
        <p className="cdm-section__label">Informations</p>

        <div className="mb-2">
          <label className="cdm-field-label">Produit <span className="text-danger">*</span></label>
          <InputProduct
            name="productId"
            allowCustomValue={true}
            handleChange={handleProductChange}
            placeholder={"Rechercher ou saisir un nom"}
            hideToggle
            validationRules={{ required: "Le produit est requis" }}
          />
        </div>

        <div className="cdm-grid-2">
          <div>
            <label className="cdm-field-label">Code-barres</label>
            <InputText name="barcode" placeholder="611 ***** *****" />
          </div>
          <div>
            <label className="cdm-field-label">Prix <span className="text-danger">*</span></label>
            <div className="cdm-price-wrapper">
              <InputText name="publicPrice" placeholder="0.00" validationRules={{ required: "Le prix est requis" }} />
              <span className="cdm-price-badge">Dhs</span>
            </div>
          </div>
        </div>

        <div className="cdm-grid-2 mt-2">
          <div>
            <label className="cdm-field-label">Prix hospitalier</label>
            <div className="cdm-price-wrapper">
              <InputText name="hospitalPrice" placeholder="0.00" />
              <span className="cdm-price-badge">Dhs</span>
            </div>
          </div>
          <div>
            <label className="cdm-field-label">Seuil d'alerte stock</label>
            <InputText name="minimumStock" type="number" placeholder="0" />
          </div>
        </div>

        <div className="cdm-grid-2 mt-2">
          <div>
            <label className="cdm-field-label">Présentation</label>
            <InputText name="presentation" placeholder="Comprimé, Sirop..." />
          </div>
          <div>
            <label className="cdm-field-label">Dosage</label>
            <InputText name="dosage" placeholder="20 MG" />
          </div>
        </div>

        <div className="cdm-grid-2 mt-2">
          <div>
            <label className="cdm-field-label">Statut</label>
            <Select name="status" options={STATUS_OPTIONS} placeholder="Choisir un statut..." />
          </div>
          <div>
            <label className="cdm-field-label">Remboursement</label>
            <Select name="refundable" options={REFUNDABLE_OPTIONS} placeholder="Remboursable ?" />
          </div>
        </div>

        <div className="mt-2">
          <label className="cdm-field-label">Laboratoire</label>
          <InputLaboratory name="laboratoryId" />
        </div>
      </div>

      <div className="cdm-section">
        <button type="button" className="cdm-optional-toggle" onClick={() => setShowMore((prev) => !prev)}>
          <FontAwesomeIcon icon={showMore ? faChevronUp : faChevronDown} />
          {showMore ? 'Masquer les informations complémentaires' : 'Informations complémentaires'}
        </button>

        {showMore && (
          <>
            <div className="cdm-grid-2 mt-2">
              <div>
                <label className="cdm-field-label">Famille</label>
                <InputText name="family" placeholder="Famille thérapeutique" />
              </div>
              <div>
                <label className="cdm-field-label">Type produit</label>
                <InputText name="productType" />
              </div>
            </div>

            <div className="cdm-grid-2 mt-2">
              <div>
                <label className="cdm-field-label">Code ATC</label>
                <InputText name="atcCode" />
              </div>
              <div>
                <label className="cdm-field-label">Tableau</label>
                <InputText name="tableLetter" />
              </div>
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Composition</label>
              <Textarea name="composition" placeholder="Composition du produit..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Indication</label>
              <Textarea name="indication" placeholder="Indications thérapeutiques..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Posologie</label>
              <Textarea name="dosagesAdministration" placeholder="Posologie et mode d'administration..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Contre-indications</label>
              <Textarea name="contraindication" placeholder="Contre-indications..." />
            </div>

            <div className="mt-2">
              <label className="cdm-field-label">Mises en garde</label>
              <Textarea name="warnings" placeholder="Mises en garde et précautions d'emploi..." />
            </div>

            <div className="cdm-grid-2 mt-2">
              <div>
                <label className="cdm-field-label">Dépendance</label>
                <InputText name="dependanceRisk" />
              </div>
              <div>
                <label className="cdm-field-label">Substance psychoactive</label>
                <InputText name="psychoactiveSubstance" />
              </div>
            </div>
          </>
        )}
      </div>

      {stockLines.map((line, index) => (
        <div className="cdm-section" key={line.uid}>
          <div className="cdm-section__header">
            <p className="cdm-section__label">Lot de stock</p>
            <div className="d-flex align-items-center gap-2">
              <span className="cdm-batch-badge">Lot #{index + 1}</span>
              <button type="button" className="cdm-remove-btn" onClick={() => onRemoveLine(line.uid)}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>

          <div className="cdm-grid-2 mb-2">
            <div>
              <label className="cdm-field-label">Numéro de lot <span className="text-danger">*</span></label>
              <InputText name={`batchNumber_${line.uid}`} placeholder="5T68" validationRules={{ required: "Le numéro de lot est requis" }} />
            </div>
            <div>
              <label className="cdm-field-label">Quantité <span className="text-danger">*</span></label>
              <InputQuantity name={`quantity_${line.uid}`} validationRules={{ required: "La quantité est requise", min: { value: 1, message: "Min 1" } }} />
            </div>
          </div>

          <div className="mb-2">
            <label className="cdm-field-label">Date d'expiration <span className="text-danger">*</span></label>
            <InputDate name={`expirationDate_${line.uid}`} mode="month-year" validationRules={{ required: "La date d'expiration est requise" }} />
          </div>

          <div>
            <label className="cdm-field-label">Description <span className="text-danger">*</span></label>
            <Textarea name={`description_${line.uid}`} placeholder="Décrire le mouvement de stock..." validationRules={{ required: "La description est requise" }} />
          </div>
        </div>
      ))}

      <button type="button" className="cdm-add-batch" onClick={onAddLine}>
        <FontAwesomeIcon icon={faPlus} />
        Ajouter un lot
      </button>
    </>
  );
}

export default function CreateProductModal({ show, onClose, defaultValues = { stockRequest: { quantity: 1 }, status: 'MARKETED' }, onSave }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [stockLines, setStockLines] = useState([]);
  const [productName, setProductName] = useState(null);

  const addLine = () => {
    setStockLines([...stockLines, { uid: uuidv4(), batchNumber: '', quantity: 1, expirationDate: '', description: '' }]);
  };

  const removeLine = (uid) => {
    setStockLines((prev) => prev.filter((line) => line.uid !== uid));
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    const formatted = stockLines.map((line) => ({
      batchNumber: data[`batchNumber_${line.uid}`],
      quantity: data[`quantity_${line.uid}`],
      expirationDate: data[`expirationDate_${line.uid}`],
      description: data[`description_${line.uid}`],
    }));

    axios.post(`${API_BASE_URL}/api/v1/products`, {
      productId: data.productId,
      barcode: data.barcode,
      publicPrice: data.publicPrice,
      status: data.status || null,
      presentation: data.presentation || null,
      refundable: data.refundable === 'true' ? true : data.refundable === 'false' ? false : null,
      dosage: data.dosage || null,
      laboratoryId: data.laboratoryId || null,
      family: data.family || null,
      productType: data.productType || null,
      hospitalPrice: data.hospitalPrice || null,
      minimumStock: data.minimumStock || null,
      atcCode: data.atcCode || null,
      tableLetter: data.tableLetter || null,
      indication: data.indication || null,
      composition: data.composition || null,
      dosagesAdministration: data.dosagesAdministration || null,
      contraindication: data.contraindication || null,
      warnings: data.warnings || null,
      dependanceRisk: data.dependanceRisk || null,
      psychoactiveSubstance: data.psychoactiveSubstance || null,
      stockLines: formatted,
    }, { withCredentials: true })
      .then((response) => {
        onClose();
        toastCreated(navigate, 'Produit enregistré avec succès !', response.data?.id && `/products/${response.data.id}`);
        onSave?.(response.data);
      })
      .catch((error) => {
        console.error('Error saving product:', error);
        toast.error(error.response?.data?.message || "Erreur lors de l'enregistrement du produit");
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Modal
      id="createProductModal"
      show={show}
      onClose={onClose}
      title={productName ? `Modifier — ${productName}` : 'Nouveau produit'}
      icon={faPills}
      iconBg="#e0f2fe"
      iconColor="#0284c7"
      footer={
        <Button isLoading={isLoading} loadingMessage="Enregistrement..." type="submit" form="createProductForm" className="btn btn-primary">
          Enregistrer le produit
        </Button>
      }
    >
      <Form id="createProductForm" onSubmit={onSubmit} defaultValues={defaultValues}>
        <ProductFormContent
          stockLines={stockLines}
          onAddLine={addLine}
          onRemoveLine={removeLine}
          onSetProductName={setProductName}
        />
      </Form>
    </Modal>
  );
}